import { Action, createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';

import * as UsersActions from './users.actions';
import { IEntityUser, IUserAccount } from '@boma/client-shared';

export const USERS_FEATURE_KEY = 'users-supplier';

export interface IArchivedEntityUser extends IEntityUser {}

export interface UserFilters {
  query: string;
  page: number;
  perPage: number;
  archived?: boolean;
}

export interface UserState extends EntityState<IEntityUser> {
  selectedId?: string | number; // which Users record has been selected
  loaded: boolean; // has the Users list been loaded
  error?: string | null; // last known error (if any)
  userEntityDetails: IEntityUser;
  createUserIdentified: IUserAccount; // When adding a user, if the phone number matches an existing account,
  pagination: {};
  perPage?: number | null;
  query?: string | null;
  archivedUsers: ArchivedUserState;
}

export interface ArchivedUserState extends EntityState<IArchivedEntityUser> {
  pagination: {
    page: number;
    perPage: number;
    totalPages: number;
    totalItems: number;
    previousPage?: number;
    nextPage?: number;
  };
}

export interface UsersPartialState {
  readonly [USERS_FEATURE_KEY]: UserState;
}

export const usersAdapter: EntityAdapter<IEntityUser> = createEntityAdapter<
  IEntityUser
>();

export const archivedUserAdapter: EntityAdapter<IArchivedEntityUser> = createEntityAdapter<
  IArchivedEntityUser
>();

export const archivedUsersInitialState: ArchivedUserState = usersAdapter.getInitialState(
  {
    pagination: {
      totalPages: 0,
      page: 1,
      perPage: 25,
      nextPage: 0,
      previousPage: 0,
      totalItems: 0,
    },
  }
);

export const initialState: UserState = usersAdapter.getInitialState({
  // set initial required properties
  loaded: false,
  createUserIdentified: null,
  userEntityDetails: null,
  pagination: {},
  archivedUsers: archivedUsersInitialState,
});

const usersReducer = createReducer(
  initialState,

  // Index
  on(UsersActions.loadUsers, (state) => ({
    ...state,
    loaded: false,
    error: null,
    userEntityDetails: null,
  })),
  on(UsersActions.loadUsersSuccess, (state, { payload }) =>
    usersAdapter.setAll(
      payload.items.map((i) => ({ id: i.user.id, ...i } as any)),
      {
        ...state,
        loaded: true,
        pagination: {
          totalItems: payload.totalItems,
          totalPages: payload.totalPages,
          previousPage: payload.previousPage,
          nextPage: payload.nextPage,
        },
      }
    )
  ),
  on(UsersActions.loadUsersFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(UsersActions.loadArchivedUsersSuccess, (state, { payload, filters }) => {
    console.log('LOADED SUCCESS', payload);
    return {
      ...state,
      archivedUsers: archivedUserAdapter.setAll(
        (payload?.items ?? []).map((i) => ({ id: i.user.id, ...i })),
        {
          ...state.archivedUsers,
          pagination: {
            page: payload.page,
            totalPages: payload.totalPages,
            perPage: filters.perPage,
            totalItems: payload.totalItems,
            nextPage: payload.nextPage,
            previousPage: payload.previousPage,
          },
        }
      ),
    };
  }),

  on(UsersActions.loadArchivedUsersFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  // Get - GET user account
  on(UsersActions.getUserEntity, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),
  on(UsersActions.getUserEntitySuccess, (state, { payload }) => ({
    ...state,
    userEntityDetails: payload,
    loaded: true,
    error: null,
  })),
  on(UsersActions.getUserEntityFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  on(UsersActions.deleteUserSuccess, (state, { userId }) => {
    const user = state.entities[userId];
    return usersAdapter.removeOne(userId, {
      ...state,
      archivedUsers: archivedUserAdapter.addOne(user, state.archivedUsers),
    });
  }),

  on(UsersActions.restoreArchivedUserSuccess, (state, { payload }) =>
    usersAdapter.addOne(payload, {
      ...state,
      archivedUsers: archivedUserAdapter.removeOne(
        payload.user.id,
        state.archivedUsers
      ),
    })
  ),

  // Create - GET account exists
  on(UsersActions.getUserAccount, (state) => ({
    ...state,
    createUserIdentified: null,
    error: null,
  })),
  on(UsersActions.getUserAccountSuccess, (state, { payload }) => ({
    ...state,
    createUserIdentified: payload,
    error: null,
  })),
  on(UsersActions.getUserAccountFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  // Update user Entity
  on(UsersActions.updateUserEntity, (state) => ({
    ...state,
    loaded: false,
    createUserIdentified: null,
    error: null,
  })),
  on(UsersActions.updateUserEntitySuccess, (state, { payload }) =>
    usersAdapter.upsertOne(payload, { ...state, loaded: true })
  ),

  // Create - POST account exists
  on(UsersActions.createUserAccountSuccess, (state, { payload }) =>
    usersAdapter.upsertOne(payload, { ...state })
  ),
  on(UsersActions.createUserAccountFailure, (state, { error }) => ({
    ...state,
    error,
  }))
);

export function userReducer(state: UserState | undefined, action: Action) {
  return usersReducer(state, action);
}
